import { Selector, createSelector } from 'reselect'

import {
  networkGroup,
} from './utils'

import {
  IData_UserSummary,
} from '@kaidam/fc-shared/src/types/schema/user_summary'

import {
  IData_CRM_Summary,
} from '@kaidam/fc-shared/src/types/crm'

import {
  INode_Org,
} from '@kaidam/fc-shared/src/types/schema/tree'

import {
  IRecord_Astonish_Template,
  IField_Astonish_Campaign_SendMode,
  IData_Astonish_Create_Campaign_Step,
  IRecord_Astonish_Campaign_Core,
  IData_Astonish_Campaign_Network_Settings,
  IData_Astonish_Project_Config,
  IRecord_Astonish_Campaign_WithJobs,
  IData_Astonish_Campaign_Summary,
  IRecord_Astonish_Campaign_WithJobs_And_Summary,
  IData_Astonish_Email_CRM,
} from '@kaidam/fc-shared/src/types/schema/astonish'

import {
  IData_Paginated_Results,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  validateEmail,
} from '@kaidam/fc-shared/src/utils/validators'

import {
  getDefaultData,
} from '@kaidam/fc-shared/src/utils/astonish'

import {
  RootState,
} from '../../types/store'

import {
  ROLE_SCOPE_SELF,
  ASTONISH_CAMPAIGN_SENDMODE_NETWORK,
  ASTONISH_CAMPAIGN_SENDMODE_SELF,
  ASTONISH_CAMPAIGN_SENDMODE_NONE,
  ASTONISH_CAMPAIGN_STATUS_SCHEDULED,
  ASTONISH_FROM_ADDRESS_META_FIELD,
  ASTONISH_FRANCHISEE_SEND_FROM_ADDRESS_MODE,
} from '@kaidam/fc-shared/src/constants'

import projectSelectors from './project'
import crmSelectors from './crm'

export const config: Selector<RootState, IData_Astonish_Project_Config> = state => state.astonish.config
export const templates: Selector<RootState, IRecord_Astonish_Template[]> = state => state.astonish.templates
export const campaigns: Selector<RootState, IRecord_Astonish_Campaign_WithJobs[]> = state => state.astonish.campaigns
export const campaignsPagination: Selector<RootState, IData_Paginated_Results> = state => state.astonish.campaignsPagination
export const createCampaignData: Selector<RootState, Partial<IRecord_Astonish_Campaign_Core> | null> = state => state.astonish.createCampaignData
export const createCampaignStep: Selector<RootState, string> = state => state.astonish.createCampaignStep
export const campaignSummary: Selector<RootState, IData_Astonish_Campaign_Summary[]> = state => state.astonish.campaignSummary
export const networkSummary: Selector<RootState, IRecord_Astonish_Campaign_WithJobs[]> = state => state.astonish.networkSummary
export const campaignCRMRecordData: Selector<RootState, IData_Astonish_Email_CRM[]> = state => state.astonish.campaignCRMRecordData
export const campaignCRMRecordPagination: Selector<RootState, IData_Paginated_Results> = state => state.astonish.campaignCRMRecordPagination
export const sendFromFranchisees: Selector<RootState, boolean> = state => state.astonish.sendFromFranchisees

const campaignSendMode = createSelector(
  projectSelectors.summary,
  projectSelectors.descendentOrgs,
  (summary, descendentOrgs): IField_Astonish_Campaign_SendMode => {
    const campaignRole = summary?.roles['astonish:campaign_admin']
    if(!campaignRole) return ASTONISH_CAMPAIGN_SENDMODE_NONE
    if(campaignRole.scope == ROLE_SCOPE_SELF) return ASTONISH_CAMPAIGN_SENDMODE_SELF
    if(descendentOrgs.length <= 0) return ASTONISH_CAMPAIGN_SENDMODE_SELF
    return ASTONISH_CAMPAIGN_SENDMODE_NETWORK
  }
)

const defaultFromAddress = createSelector(
  config,
  (config) => config.from_address,
)

const fromDomains = createSelector(
  config,
  (config) => config.from_domains,
)

const defaultCreateCampaignData = createSelector(
  projectSelectors.summary,
  campaignSendMode,
  defaultFromAddress,
  (userSummary, campaignSendMode, defaultFromAddress): Partial<IRecord_Astonish_Campaign_Core> => {
    return getDefaultData(
      campaignSendMode,
      userSummary?.org,
      defaultFromAddress,
    )
  }
)

const CREATE_CAMPAIGN_STEPS: IData_Astonish_Create_Campaign_Step[] = [{
  id: 'settings',
  title: 'Settings',
  description: 'Edit the name, from address, subject line and notes for the campaign.',
}, {
  id: 'network',
  title: 'Network',
  description: 'Choose who in the network you are sending the email for.',
}, {
  id: 'template',
  title: 'Template',
  description: 'Select and edit the template for the email.',
}, {
  id: 'query',
  title: 'Recipients',
  description: 'Filter the CRM contacts that are recipients for the email.',
}, {
  id: 'schedule',
  title: 'Schedule',
  description: 'Schedule the email to be sent now or in the future.',
}]

const createCampaignSteps = createSelector(
  createCampaignData,
  campaignSendMode,
  (data, campaignSendMode): IData_Astonish_Create_Campaign_Step[] => {
    return CREATE_CAMPAIGN_STEPS.filter(step => {
      if(campaignSendMode == ASTONISH_CAMPAIGN_SENDMODE_SELF && step.id == 'network') return false
      if(data?.status == ASTONISH_CAMPAIGN_STATUS_SCHEDULED) {
        if(step.id == 'network') return false
        if(step.id == 'query') return false
      }
      return true
    })
  }
)

export interface IData_Astonish_Campaign_Recipients_Summary {
  id: string,
  name: string,
  count: number,
}

const calculateCampaignRecipientsSummary = (
  summary: IData_UserSummary,
  querySummary: IData_CRM_Summary[],
  orgs: INode_Org[],
  networkSettings?: IData_Astonish_Campaign_Network_Settings,
): IData_Astonish_Campaign_Recipients_Summary[] => {
  if(!networkSettings) return []
  if(!querySummary) return []
  const summaryById = querySummary.reduce<Record<string, number>>((all, item) => {
    all[item.org] = item.count
    return all
  }, {})

  if(networkSettings.mode == ASTONISH_CAMPAIGN_SENDMODE_NETWORK) {
    const org_ids = networkSettings.org_ids || []
    return orgs
      .filter(org => org_ids.indexOf(org.id) >= 0)
      .map(org => {
        const count = summaryById[org.id] || 0
        return {
          id: org.id,
          name: org.name,
          count,
        }
      })
  }
  else {
    const count = summaryById[summary?.org || ''] || 0
    return [{
      id: summary?.orgNode.id || '',
      name: summary?.orgNode.name || '',
      count,
    }]
  }
}

const campaignRecipientsSummary = createSelector(
  projectSelectors.summary,
  crmSelectors.querySummary,
  projectSelectors.descendentOrgsWithOrg,
  createCampaignData,
  (summary, querySummary, orgs, data): IData_Astonish_Campaign_Recipients_Summary[] => {
    if(!summary) return []
    return calculateCampaignRecipientsSummary(
      summary,
      querySummary,
      orgs,
      data?.network,
    )
  }
)

const campaignRecipientsSummaryVariableCampaign = () => createSelector(
  projectSelectors.summary,
  crmSelectors.querySummary,
  projectSelectors.descendentOrgsWithOrg,
  (state: RootState, networkSettings: IData_Astonish_Campaign_Network_Settings) => networkSettings,
  (summary, querySummary, orgs, networkSettings): IData_Astonish_Campaign_Recipients_Summary[] => {
    if(!summary) return []
    return calculateCampaignRecipientsSummary(
      summary,
      querySummary,
      orgs,
      networkSettings,
    )
  }
)

const totalCampaignRecipients = createSelector(
  campaignRecipientsSummary,
  (summary): number => {
    return summary.reduce<number>((all, item) => {
      return all + item.count
    }, 0)
  }
)

// Generic function to calculate campaign progress errors
const calculateCampaignProgressErrors = (
  data: Partial<IRecord_Astonish_Campaign_Core> | null,
  recipients: number,
  fromDomains: string[],
  enableFromEmailValidation: boolean
): Record<string, string> => {
  const errors: Record<string, string> = {
    'network.org_ids': '',
    'settings.from_address': '',
    'settings.subject': '',
    'settings.name': '',
    'template.data': '',
    'query.recipients': '',
  };

  if (data?.network?.mode === ASTONISH_CAMPAIGN_SENDMODE_NETWORK && (data?.network?.org_ids || []).length <= 0) {
    errors['network.org_ids'] = 'You must select at least one org';
  }

  if (enableFromEmailValidation) {
    if (!data?.config?.from_address) {
      errors['settings.from_address'] = 'Please enter a from address';
    } else if (!validateEmail(data.config.from_address)) {
      errors['settings.from_address'] = 'Please enter a valid email address';
    } else {
      const fromAddress = data.config.from_address;
      const foundDomainContains = fromDomains.find(domain => fromAddress.indexOf(domain) >= 0);
      if (!foundDomainContains) {
        errors['settings.from_address'] = 'The from address must use one of the verified domains';
      }
    }
  } else {
    // Remove the 'settings.from_address' error when email validation is disabled
    delete errors['settings.from_address'];
  }

  if (!data?.config?.name) {
    errors['settings.name'] = 'Please enter a campaign name';
  }
  if (!data?.config?.subject) {
    errors['settings.subject'] = 'Please enter a subject line';
  }
  if (!data?.layout || !data.layout.rows || data.layout.rows.length <= 0) {
    errors['template.data'] = 'Please choose a layout';
  }

  const tags = data?.crm_query?.tags || [];
  const all = data?.crm_query?.all || false;
  if (recipients <= 0 || (tags.length <= 0 && !all)) {
    errors['query.recipients'] = 'There are no recipients selected';
  }

  return errors;
};

// Selector with email validation
const campaignProgressErrors = createSelector(
  createCampaignData,
  totalCampaignRecipients,
  fromDomains,
  sendFromFranchisees,
  createCampaignStep,
  (data, recipients, fromDomains, sendFromFranchisees, currentStep) => {
    const errors: Record<string, string> = {};

    if (!sendFromFranchisees) {
      if (!data?.config?.from_address) {
        errors['settings.from_address'] = 'Please enter a from address';
      } else if (data.config.from_address !== ASTONISH_FRANCHISEE_SEND_FROM_ADDRESS_MODE && !validateEmail(data.config.from_address)) {
        errors['settings.from_address'] = 'Please enter a valid email address';
      } else {
        const fromAddress = data.config.from_address;
        const foundDomainContains = fromDomains.find(domain => fromAddress.indexOf(domain) >= 0);
        if (!foundDomainContains && fromAddress !== ASTONISH_FRANCHISEE_SEND_FROM_ADDRESS_MODE) {
          errors['settings.from_address'] = 'The from address must use one of the verified domains';
        }
      }
    }

    // Add other error checks here (e.g., for name and subject)
    if (!data?.config?.name) {
      errors['settings.name'] = 'Please enter a campaign name';
    }
    if (!data?.config?.subject) {
      errors['settings.subject'] = 'Please enter a subject line';
    }

    if (currentStep === 'network' && data?.network?.mode === ASTONISH_CAMPAIGN_SENDMODE_NETWORK && data.network.org_ids && data.network.org_ids.length === 0) {
      errors['network.org_ids'] = 'Please select at least one organization';
    }

    return errors;
  }
);

const campaignProgressErrorsPerSection = createSelector(
  campaignProgressErrors,
  (campaignProgressErrors): Record<string, number> => {
    return Object
      .keys(campaignProgressErrors)
      .reduce<Record<string, number>>((all, name) => {
        const [ section ] = name.split('.')
        const currentValue = all[section] || 0
        all[section] = currentValue + (campaignProgressErrors[name] ? 1 : 0)
        return all
      }, {})
  }
)

const campaignProgressState = createSelector(
  createCampaignData,
  createCampaignStep,
  createCampaignSteps,
  campaignProgressErrors,
  sendFromFranchisees,
  totalCampaignRecipients,
  (data, step, steps, errors, sendFromFranchisees, totalRecipients): {
    back: boolean,
    forward: boolean,
  } => {
    const currentIndex = steps.findIndex(s => s.id == step)
    let back = currentIndex > 0
    let forward = false

    if(data) {
      if(step == 'settings') {
        const settingsErrors = Object.keys(errors).filter(key => key.startsWith('settings.')).length;
        forward = settingsErrors === 0;
      }
      else if(step == 'network' && data?.network) {
        const networkMode = data.network.mode;
        const networkOrgIds = data.network.org_ids || [];
        const networkErrors = Object.keys(errors).filter(key => key.startsWith('network.')).length;
        
        if(networkMode != ASTONISH_CAMPAIGN_SENDMODE_NONE && networkOrgIds.length > 0 && networkErrors === 0) {
          forward = true;
        }
      }
      else if(step == 'template') {
        const templateErrors = Object.keys(errors).filter(key => key.startsWith('template.')).length;
        const hasTemplate = data.layout && data.layout.rows && data.layout.rows.length > 0;
        forward = templateErrors === 0 && hasTemplate;
      }
      else if(step == 'query' && data?.crm_query) {
        const queryErrors = Object.keys(errors).filter(key => key.startsWith('query.')).length;
        const hasTags = data.crm_query.tags && data.crm_query.tags.length > 0;
        const sendToAll = data.crm_query.all === true;
        forward = queryErrors === 0 && (hasTags || sendToAll) && totalRecipients > 0;
      }
      else if(step == 'schedule') {
        // Add logic for schedule step if needed
        forward = true;
      }
    }
    return {
      back,
      forward,
    }
  }
)

const campaignSummaryMerged = createSelector(
  projectSelectors.currentOrgMap,
  campaignSummary,
  networkSummary,
  (orgMap, campaignSummary, networkSummary): IRecord_Astonish_Campaign_WithJobs_And_Summary[] => {
    const summaryByOrg = campaignSummary.reduce<Record<string, IData_Astonish_Campaign_Summary>>((all, summary) => {
      all[summary.org] = summary
      return all
    }, {})
    return networkSummary
      .map(campaign => {
        const summary: IData_Astonish_Campaign_Summary = summaryByOrg[campaign.org] ?
          summaryByOrg[campaign.org] :
          {
            org: campaign.org,
            data: {},
          }
        const org = orgMap[campaign.org]
        if(!org) return null
        const ret: IRecord_Astonish_Campaign_WithJobs_And_Summary = Object.assign({}, campaign, {
          summary,
          orgName: org.name,
        })
        return ret
      })
      .filter(r => r)
    
  }
)

const templateMap = createSelector(
  templates,
  (templates) => {
    return templates.reduce<Record<string, IRecord_Astonish_Template>>((all, template) => {
      all[template.id] = template
      return all
    }, {})
  }
)

export const network = networkGroup('astonish', [
  'loadTemplates',
  'templateForm',
  'campaignForm',
  'deleteTemplate',
  'loadCampaignCRMRecordData',
  'loadCampaigns',
])

export default {
  templates,
  templateMap,
  campaigns,
  campaignsPagination,
  defaultFromAddress,
  fromDomains,
  campaignSummary,
  campaignCRMRecordData,
  campaignCRMRecordPagination,
  createCampaignData,
  createCampaignStep,
  defaultCreateCampaignData,
  campaignSendMode,
  createCampaignSteps,
  campaignProgressState,
  campaignProgressErrors,
  campaignProgressErrorsPerSection,
  campaignRecipientsSummary,
  campaignRecipientsSummaryVariableCampaign,
  totalCampaignRecipients,
  campaignSummaryMerged,
  network,
  sendFromFranchisees,
}
